import { isDefined } from "@clipboard-health/util-ts";
import {
  type FcfFeedItem,
  useFcfKnockFeedMessages,
} from "@src/appV2/Knock/hooks/useFcfKnockFeedMessages";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useCallback, useEffect, useState } from "react";

import { FcfReplacementShiftsBottomSheet } from "./FcfReplacementShiftsBottomSheet";

/**
 * Watches the Knock in-app feed for unread FCF cancellation messages and
 * auto-triggers the replacement-shifts modal when one is found. Expiry (4 h)
 * and sequential queue logic live inside `useFcfKnockFeedMessages`.
 *
 * Must be rendered inside KnockFeedProvider — callers are responsible for
 * gating on Knock token availability.
 */
export function FcfFeedAutoTrigger() {
  const modalState = useModalState("closed");
  const { modalIsOpen, openModal, closeModal } = modalState;
  const { currentItem, onDismiss, onInteract, onArchive } = useFcfKnockFeedMessages();

  // activeItem tracks what's currently shown in the modal. It's separate from
  // currentItem so the modal stays open even when Knock removes an archived item
  // from the local store (which would clear currentItem).
  const [activeItem, setActiveItem] = useState<FcfFeedItem | undefined>(undefined);

  useEffect(() => {
    if (isDefined(currentItem) && !modalIsOpen) {
      setActiveItem(currentItem);
      openModal();
    }
  }, [currentItem, modalIsOpen, openModal]);

  const activeItemId = activeItem?.message.id;

  const handleDismiss = useCallback((): void => {
    setActiveItem(undefined);
    // Guard against accidentally dismissing the next queued item when the
    // Knock store already advanced currentItem (e.g. after onNoEligibleShifts).
    if (currentItem?.message.id === activeItemId) {
      void onDismiss();
    }
    closeModal();
  }, [activeItemId, closeModal, currentItem, onDismiss]);

  const handleNoEligibleShifts = useCallback((): void => {
    // Archive server-side so the message won't re-appear on next app open,
    // but don't clear activeItem — the modal stays open showing the empty state.
    void onArchive();
  }, [onArchive]);

  if (!isDefined(activeItem)) {
    return null;
  }

  return (
    <FcfReplacementShiftsBottomSheet
      modalState={modalState}
      params={activeItem.params}
      onDismiss={handleDismiss}
      onInteract={onInteract}
      onNoEligibleShifts={handleNoEligibleShifts}
    />
  );
}
